import { Link } from 'react-router-dom';
import { SERVICE_LINKS } from '../../data/marketingContent.js';
import { appUrl } from '../../utils/publicUrls.js';
import { IconChevronDown } from '../icons/Icons.jsx';

export function BrandMark({ tagline = false, size = 'md' }) {
  const box = size === 'lg' ? 'h-11 w-11 text-lg' : 'h-9 w-9 text-base';
  return (
    <span className="flex min-w-0 items-center gap-3">
      <span className={`${box} flex flex-shrink-0 items-center justify-center rounded-[10px] bg-mk-primary font-bold text-white shadow-[0_6px_16px_rgba(85,87,232,.28)]`}>
        I
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[17px] font-semibold leading-tight text-mk-ink">Ineedlead CRM</span>
        {tagline && <span className="block truncate text-xs text-mk-muted">Messaging and automation workspace</span>}
      </span>
    </span>
  );
}

function ServicesMenu() {
  return (
    <div className="group relative hidden lg:block">
      <Link to="/services" className="inline-flex items-center gap-1 rounded-[10px] px-3 py-2 text-sm font-semibold text-mk-muted transition hover:bg-mk-bg hover:text-mk-ink">
        Services <IconChevronDown size={15} />
      </Link>
      <div className="invisible absolute left-1/2 top-full z-50 w-[720px] -translate-x-1/2 pt-3 opacity-0 transition group-hover:visible group-hover:opacity-100">
        <div className="rounded-[14px] border border-mk-line bg-white p-3 shadow-[0_24px_64px_rgba(15,23,42,.16)]">
          <div className="grid grid-cols-2 gap-1">
            {SERVICE_LINKS.slice(0, 6).map((service) => (
              <Link key={service.slug} to={`/services/${service.slug}`} className="rounded-[10px] p-3 transition hover:bg-mk-bg">
                <p className="text-sm font-semibold text-mk-ink">{service.title}</p>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-mk-muted">{service.summary}</p>
              </Link>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between rounded-[10px] bg-mk-soft px-4 py-3">
            <p className="text-sm font-semibold text-mk-softText">Need every channel together?</p>
            <Link to="/services/all-in-one-communication-crm" className="text-sm font-bold text-mk-primary hover:text-mk-hover">
              View the platform →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SiteHeader({ onGetStarted }) {
  const ctaClass = 'inline-flex min-h-[40px] items-center justify-center rounded-[10px] bg-mk-primary px-4 text-sm font-bold text-white shadow-[0_5px_14px_rgba(85,87,232,.2)] transition hover:-translate-y-px hover:bg-mk-hover';
  return (
    <header className="sticky top-0 z-40 border-b border-mk-line bg-white/90 px-4 backdrop-blur sm:px-6">
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4">
        <Link to="/" className="min-w-0" aria-label="Ineedlead CRM home">
          <BrandMark />
        </Link>
        <div className="flex items-center gap-1 sm:gap-2">
          <ServicesMenu />
          <Link to="/blog" className="hidden rounded-[10px] px-3 py-2 text-sm font-semibold text-mk-muted transition hover:bg-mk-bg hover:text-mk-ink md:inline-flex">
            Blog
          </Link>
          <a href={appUrl('/login')} className="hidden min-h-[40px] items-center rounded-[10px] border border-mk-line bg-white px-4 text-sm font-semibold text-mk-ink transition hover:border-mk-strong hover:bg-mk-bg sm:inline-flex">
            Sign in
          </a>
          {onGetStarted ? (
            <button type="button" onClick={onGetStarted} className={ctaClass}>Get started</button>
          ) : (
            <Link to="/#builder" className={ctaClass}>Get started</Link>
          )}
        </div>
      </nav>
    </header>
  );
}

const FOOTER_GROUPS = [
  ['Product', [
    ['/services/bulk-sms-crm', 'Bulk SMS CRM'],
    ['/services/whatsapp-api-crm', 'WhatsApp API CRM'],
    ['/services/ivr-call-routing', 'Calls and IVR'],
    ['/services/ai-automation-builder', 'AI automation'],
  ]],
  ['Resources', [
    ['/services', 'All services'],
    ['/blog', 'Blog'],
    ['/mobile-app', 'Mobile app'],
  ]],
  ['Legal', [
    ['/privacy', 'Privacy'],
    ['/terms', 'Terms'],
    ['/refund-policy', 'Refunds'],
    ['/acceptable-use', 'Acceptable use'],
    ['/sms-terms', 'SMS terms'],
  ]],
];

export function SiteFooter() {
  return (
    <footer className="border-t border-mk-line bg-white px-4 sm:px-6">
      <div className="mx-auto grid max-w-7xl gap-10 py-12 md:grid-cols-[1.4fr_repeat(3,1fr)]">
        <div>
          <BrandMark />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-mk-muted">
            Bulk SMS, WhatsApp, calls, email and AI automation in one private workspace.
          </p>
          <a href={appUrl('/login')} className="mt-5 inline-flex text-sm font-bold text-mk-primary hover:text-mk-hover">
            Sign in to your workspace →
          </a>
        </div>
        {FOOTER_GROUPS.map(([title, links]) => (
          <div key={title}>
            <p className="text-[11px] font-semibold uppercase tracking-[.08em] text-mk-muted">{title}</p>
            <ul className="mt-4 space-y-2.5">
              {links.map(([to, label]) => (
                <li key={to}>
                  <Link to={to} className="text-sm text-mk-ink transition hover:text-mk-primary">{label}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mx-auto flex max-w-7xl flex-col gap-2 border-t border-mk-line py-6 text-xs text-mk-muted sm:flex-row sm:items-center sm:justify-between">
        <p>© {new Date().getFullYear()} Ineedlead CRM. All rights reserved.</p>
        <p>Built for opted-in customer messaging.</p>
      </div>
    </footer>
  );
}

// Shared typographic pieces so every public page reads the same.
export function Eyebrow({ children, className = '' }) {
  return <p className={`text-xs font-bold uppercase tracking-[.08em] text-mk-softText ${className}`}>{children}</p>;
}

export function SectionTitle({ children, className = '' }) {
  return <h2 className={`text-[32px] font-bold leading-[1.1] tracking-[-0.02em] text-mk-ink sm:text-[44px] ${className}`}>{children}</h2>;
}
